import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { hasTrackingParams } from "@/lib/tracking-params";

/**
 * Catch-all. Its main job is ad resilience: if an ad URL points at a path that
 * does not exist (typo, stale path, extra segment) but carries tracking
 * parameters, send the click to the funnel WITH its query string instead of
 * burning a paid click on a 404 and losing the ad-set attribution.
 */
export const Route = createFileRoute("/$")({
  beforeLoad: ({ location }) => {
    const search = location.searchStr ?? "";
    if (hasTrackingParams(search)) {
      throw redirect({ href: `/apply${search}` });
    }
  },
  head: () => ({
    meta: [
      { title: "Page not found | INFLOW Marketing" },
      { name: "description", content: "This page doesn't exist. Head back to the INFLOW Marketing application page." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: NotFound,
});

function NotFound() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <Link
          to="/apply"
          className="mt-6 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          Go to the application
        </Link>
      </div>
    </div>
  );
}
